import React from 'react';
import { motion } from 'framer-motion';
import { coreValues } from '../data/mock';
import { Lightbulb, ShieldCheck, Award, Users } from 'lucide-react';

const iconMap = {
  Lightbulb: Lightbulb,
  ShieldCheck: ShieldCheck,
  Award: Award,
  Users: Users
};

const CoreValues = () => {
    
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  return (
    <section className="py-16 lg:py-24 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <motion.span 
            className="inline-block px-4 py-1 rounded-full bg-blue-50 text-blue-900 font-medium text-sm mb-4"
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Our Culture
          </motion.span>
          <motion.h2 
            className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6"
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            Core Values That Drive Us
          </motion.h2>
          <motion.p 
            className="text-lg text-gray-600"
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            These principles guide our decisions and shape how we interact with our clients and each other.
          </motion.p>
        </div>

        <motion.div 
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-8"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
        >
          {coreValues.map((value, index) => {
            const IconComponent = iconMap[value.icon];
            
            // Define gradients for each item
            const gradients = [
              "from-blue-500 to-cyan-500",
              "from-purple-500 to-pink-500", 
              "from-amber-500 to-orange-500",
              "from-emerald-500 to-green-500"
            ];
            const gradient = gradients[index % gradients.length];
            const shadowColor = [
                "shadow-blue-200",
                "shadow-purple-200",
                "shadow-orange-200",
                "shadow-green-200"
            ][index % 4];

            return (
              <motion.div 
                key={index}
                variants={itemVariants}
                className="bg-white rounded-2xl p-8 hover:shadow-xl transition-all duration-300 border border-gray-100 group relative overflow-hidden"
              >
                <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-6 bg-gradient-to-br ${gradient} text-white shadow-lg ${shadowColor} group-hover:scale-110 transition-transform duration-300`}>
                  <IconComponent size={28} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3>
                <p className="text-gray-600 leading-relaxed">
                  {value.description}
                </p>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

export default CoreValues;
